import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Stack } from '@mui/material'
import dayjs from 'dayjs'
import {
  getEnrollmentConfig,
  createEnrollmentContact,
  updateEnrollmentContact,
  completeEnrollment,
  resetEnrollment,
  setContactId,
} from '../../features/selfEnrollment/selfEnrollment'
import { useMyContextLoading } from '../../components/LoadingContextProvider/LoadingContextProvider'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import PersonalInfoStep from './steps/PersonalInfoStep'
import DebtInfoStep from './steps/DebtInfoStep'
import TermSelectionStep from './steps/TermSelectionStep'
import ReviewStep from './steps/ReviewStep'
import EnrollmentBlocked, { blockedMessageOf } from './EnrollmentBlocked'
import {
  ContactBasicInfo,
  ContactCreditSelect,
  ContactProgramPlan,
  ContactReview
} from '../../components/Icons/Icons'
import Banner from '../../assets/images/SuccessSigning.jpg'
import Logo from '../../assets/images/logo.png'
import { useThemeContext } from '../../theme/ThemeContext'
import { setLinkToken } from '../../utils/linkToken'

const getTabs = (activeStep) => [
  { id: 'personal-info', label: 'Personal Info', icon: ContactBasicInfo, disabled: false },
  { id: 'debt-info', label: 'Debt Info', icon: ContactCreditSelect, disabled: activeStep < 1 },
  { id: 'term-selection', label: 'Select Term', icon: ContactProgramPlan, disabled: activeStep < 2 },
  { id: 'review', label: 'Review', icon: ContactReview, disabled: activeStep < 3 },
]

const SelfEnrollment = () => {
  const { affiliateId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()

  const { config, loading, submitting, contactId, debts } = useSelector(state => state.selfEnrollment)
  const { mode } = useThemeContext()

  const [showLanding, setShowLanding] = useState(true)
  const [activeStep, setActiveStep] = useState(0)
  const [selectedTab, setSelectedTab] = useState('personal-info')

  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    primaryPhone: '',
    gender: '',
    address: '',
    aptNumber: '',
    city: '',
    state: '',
    zip: '',
    dateOfBirth: '',
    ssn: '',
  })

  const [savedPersonalInfo, setSavedPersonalInfo] = useState(null)

  const [hasCoApplicant, setHasCoApplicant] = useState(false)
  const [coApplicantMode, setCoApplicantMode] = useState('create')
  const [coApplicantInfo, setCoApplicantInfo] = useState({
    firstName: '', lastName: '', middleName: '', email: '',
    primaryPhone: '', ssNumber: '', dateOfBirth: '', gender: '',
    address: '', aptNumber: '', city: '', state: '', zip: '',
  })
  const [coApplicantId, setCoApplicantId] = useState(null)
  const [linkedCoApplicantId, setLinkedCoApplicantId] = useState(null)

  const [selectedDebts, setSelectedDebts] = useState([])
  const [useManualEntry, setUseManualEntry] = useState(false)
  const [selectedTerm, setSelectedTerm] = useState(null)
  const [frequency, setFrequency] = useState('monthly')
  const [startDate, setStartDate] = useState(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const year = tomorrow.getFullYear()
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0')
    const day = String(tomorrow.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })

  const STORAGE_KEY = `enrollment_${affiliateId}`
  const OFFICE_KEY = `enrollment_office_${affiliateId}`

  // A link generated from the CRM carries ?office=; otherwise the prospect picks once and
  // the choice survives a refresh mid-flow.
  const [selectedGroupId, setSelectedGroupId] = useState(() => {
    try {
      return searchParams.get('office') || localStorage.getItem(OFFICE_KEY) || null
    } catch (e) {
      return searchParams.get('office') || null
    }
  })

  const chooseOffice = (officeId) => {
    try {
      localStorage.setItem(OFFICE_KEY, officeId)
    } catch (e) {}
    setSelectedGroupId(officeId)
  }

  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY)
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData)
        if (parsed.contactId) {
          setShowLanding(false)
          dispatch(setContactId(parsed.contactId))
          setActiveStep(parsed.activeStep || 0)
          setSelectedTab(parsed.selectedTab || 'personal-info')
          if (parsed.personalInfo) setPersonalInfo(parsed.personalInfo)
          if (parsed.selectedDebts) setSelectedDebts(parsed.selectedDebts)
          if (parsed.useManualEntry !== undefined) setUseManualEntry(parsed.useManualEntry)
          if (parsed.selectedTerm) setSelectedTerm(parsed.selectedTerm)
          if (parsed.frequency) setFrequency(parsed.frequency)
          if (parsed.startDate) setStartDate(parsed.startDate)
        }
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [affiliateId])

  const [configError, setConfigError] = useState(false)
  const [blockedMessage, setBlockedMessage] = useState(null)

  useEffect(() => {
    if (contactId) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        contactId,
        activeStep,
        selectedTab,
        personalInfo,
        selectedDebts,
        useManualEntry,
        selectedTerm,
        frequency,
        startDate,
      }))
    }
  }, [contactId, activeStep, selectedTab, personalInfo, selectedDebts, useManualEntry, selectedTerm, frequency, startDate])

  useEffect(() => {
    const fetchConfig = async () => {
      triggerLoading(true)
      const result = await dispatch(getEnrollmentConfig({ affiliateId, selectedGroupId }))
      triggerLoading(false)
      if (getEnrollmentConfig.rejected.match(result)) {
        setConfigError(true)
        showSnackBar('error', result.payload || 'Invalid enrollment link')
      }
    }
    fetchConfig()
  }, [affiliateId, selectedGroupId])

  useEffect(() => {
    if (selectedDebts.length > 0) {
      const allDebts = [...(debts?.eligible || []), ...(debts?.ineligible || [])]
      if (allDebts.length > 0) {
        setSelectedDebts(prev => prev.map(selected => {
          const updated = allDebts.find(d => d._id === selected._id)
          return updated ? { ...selected, balanceAmount: updated.balanceAmount } : selected
        }))
      }
    }
  }, [debts])

  const handlePersonalInfoNext = async () => {
    triggerLoading(true)

    if (contactId) {
      const hasChanges = JSON.stringify(personalInfo) !== JSON.stringify(savedPersonalInfo)
      if (!hasChanges) {
        setActiveStep(Math.max(activeStep, 1))
        setSelectedTab('debt-info')
        triggerLoading(false)
        return
      }
      const result = await dispatch(updateEnrollmentContact({ contactId, personalInfo }))
      if (updateEnrollmentContact.fulfilled.match(result)) {
        setSavedPersonalInfo({ ...personalInfo })
        setActiveStep(Math.max(activeStep, 1))
        setSelectedTab('debt-info')
      } else {
        showSnackBar('error', result.payload || 'Failed to update personal information')
      }
      triggerLoading(false)
      return
    }

    let coApplicantPayload = null
    if (hasCoApplicant) {
      if (coApplicantMode === 'link' && linkedCoApplicantId) {
        coApplicantPayload = { linkExistingId: linkedCoApplicantId }
      } else {
        coApplicantPayload = {
          firstName: coApplicantInfo.firstName,
          lastName: coApplicantInfo.lastName,
          middleName: coApplicantInfo.middleName,
          email: coApplicantInfo.email,
          primaryPhone: coApplicantInfo.primaryPhone,
          ssNumber: coApplicantInfo.ssNumber,
          dateOfBirth: coApplicantInfo.dateOfBirth,
          gender: coApplicantInfo.gender,
          address: {
            address: coApplicantInfo.address,
            aptNumber: coApplicantInfo.aptNumber,
            city: coApplicantInfo.city,
            state: coApplicantInfo.state,
            zip: coApplicantInfo.zip,
          },
        }
      }
    }

    const result = await dispatch(createEnrollmentContact({
      affiliateId,
      personalInfo,
      coApplicantInfo: coApplicantPayload,
      selectedGroupId: selectedGroupId || config?.groupId || null,
    }))

    if (createEnrollmentContact.fulfilled.match(result) && blockedMessageOf(result.payload)) {
      localStorage.removeItem(STORAGE_KEY)
      setBlockedMessage(blockedMessageOf(result.payload))
    } else if (createEnrollmentContact.fulfilled.match(result)) {
      setSavedPersonalInfo({ ...personalInfo })
      if (result.payload?.data?.coApplicantId) {
        setCoApplicantId(result.payload.data.coApplicantId)
      }
      setActiveStep(Math.max(activeStep, 1))
      setSelectedTab('debt-info')
    } else {
      showSnackBar('error', result.payload || 'Failed to save personal information')
    }
    triggerLoading(false)
  }

  const handleDebtInfoNext = () => {
    setActiveStep(Math.max(activeStep, 2))
    setSelectedTab('term-selection')
  }

  const handleTermSelectionNext = () => {
    setActiveStep(Math.max(activeStep, 3))
    setSelectedTab('review')
  }

  const handleBack = (prevTab) => {
    setSelectedTab(prevTab)
  }

  const handleSubmit = async () => {
    triggerLoading(true)

    const result = await dispatch(completeEnrollment({
      contactId,
      selectedDebtIds: selectedDebts.map(d => d._id),
      selectedTerm,
      frequency,
      startDate,
      creditType: useManualEntry ? 'creditReportManually' : 'softCreditCheck',
    }))
    triggerLoading(false)

    if (completeEnrollment.fulfilled.match(result)) {
      localStorage.removeItem(STORAGE_KEY)
      const linkToken = result.payload?.data?.linkToken
      if (linkToken) setLinkToken(linkToken, `/bank-account/${contactId}`)
      navigate(`/bank-account/${contactId}`, { state: { source: 'enrollment' } })
    } else {
      showSnackBar('error', result.payload || 'Submission failed')
    }
  }

  if (configError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="border border-divider bg-surface shadow-lg rounded-2xl p-12 text-center max-w-md">
          <div className="text-6xl mb-4">🔗</div>
          <div className="text-xl font-bold text-text-primary mb-2">Invalid Enrollment Link</div>
          <p className="text-text-secondary">
            This enrollment link is invalid or has expired. Please contact your representative for a new link.
          </p>
        </div>
      </div>
    )
  }

  if (blockedMessage) {
    return <EnrollmentBlocked message={blockedMessage} />
  }

  if (loading || !config) {
    return null
  }

  if (showLanding) {
    return (
      <div className="flex max-h-[100vh] w-full">
        <div className="w-full h-screen overflow-hidden max-w-[708px] hidden md:block">
          <img src={Banner} alt="overlay" className="h-screen w-full object-cover object-bottom" />
        </div>
        <div className="w-full overflow-y-auto h-screen">
          <div className="ml-[40px] mt-[40px] mr-[12px] w-[50px]">
            <img src={Logo} alt="logo" className={mode === 'dark' ? 'brightness-0 invert' : ''} />
          </div>
          <div className="min-h-[calc(100vh-120px)] w-full flex justify-center items-center">
            <div className="text-center px-6 py-8 max-w-lg">
              <h1 className="text-[1.5rem] md:text-5xl font-bold text-text-primary mb-4 leading-tight">
                This is where your<br />debt-free journey begins.
              </h1>
              <p className="text-lg text-text-secondary mb-8">
                Sign up to take the first step toward becoming debt-free.
              </p>
              <button
                onClick={() => setShowLanding(false)}
                className="bg-primary-main text-primary-contrast font-semibold text-lg px-10 py-3.5 rounded-xl hover:opacity-90 transition-opacity w-full max-w-sm"
              >
                Get Started
              </button>
              <p className="text-xs text-text-secondary mt-4">
                Takes less than 5 minutes to complete
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (config?.requiresSelection) {
    return (
      <div className="flex max-h-[100vh] w-full">
        <div className="w-full h-screen overflow-hidden max-w-[708px] hidden md:block">
          <img src={Banner} alt="overlay" className="h-screen w-full object-cover object-bottom" />
        </div>
        <div className="w-full overflow-y-auto h-screen">
          <div className="ml-[40px] mt-[40px] mr-[12px] w-[50px]">
            <img src={Logo} alt="logo" className={mode === 'dark' ? 'brightness-0 invert' : ''} />
          </div>
          <div className="min-h-[calc(100vh-120px)] w-full flex justify-center items-center">
            <div className="px-6 py-8 max-w-lg w-full">
              <h1 className="text-[1.5rem] md:text-4xl font-bold text-text-primary mb-3 text-center">
                Which office are you enrolling with?
              </h1>
              <p className="text-text-secondary mb-8 text-center">
                {config?.affiliateName} works with more than one office. Choose the one that invited you.
              </p>
              <div className="space-y-3">
                {(config?.offices || []).map((office) => (
                  <button
                    key={office._id}
                    onClick={() => chooseOffice(office._id)}
                    className="w-full text-left p-4 rounded-xl border border-divider/20 hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <div className="font-semibold text-text-primary">{office.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const tabs = getTabs(activeStep)

  const totalDebt = selectedDebts.reduce((sum, d) => sum + (d.balanceAmount || 0), 0)

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'personal-info':
        return (
          <PersonalInfoStep
            personalInfo={personalInfo}
            setPersonalInfo={setPersonalInfo}
            onNext={handlePersonalInfoNext}
            hasCoApplicant={hasCoApplicant}
            setHasCoApplicant={setHasCoApplicant}
            coApplicantMode={coApplicantMode}
            setCoApplicantMode={setCoApplicantMode}
            coApplicantInfo={coApplicantInfo}
            setCoApplicantInfo={setCoApplicantInfo}
            linkedCoApplicantId={linkedCoApplicantId}
            setLinkedCoApplicantId={setLinkedCoApplicantId}
          />
        )
      case 'debt-info':
        return (
          <DebtInfoStep
            selectedDebts={selectedDebts}
            setSelectedDebts={setSelectedDebts}
            useManualEntry={useManualEntry}
            setUseManualEntry={setUseManualEntry}
            onNext={handleDebtInfoNext}
            onBack={() => handleBack('personal-info')}
          />
        )
      case 'term-selection':
        return (
          <TermSelectionStep
            config={config}
            selectedDebts={selectedDebts}
            selectedTerm={selectedTerm}
            setSelectedTerm={setSelectedTerm}
            frequency={frequency}
            setFrequency={setFrequency}
            startDate={startDate}
            setStartDate={setStartDate}
            onNext={handleTermSelectionNext}
            onBack={() => handleBack('debt-info')}
          />
        )
      case 'review':
        return (
          <ReviewStep
            personalInfo={personalInfo}
            selectedDebts={selectedDebts}
            selectedTerm={selectedTerm}
            frequency={frequency}
            config={config}
            onSubmit={handleSubmit}
            onBack={() => handleBack('term-selection')}
            submitting={submitting}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-background py-4 md:py-8 px-2 md:px-4">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-[20px] md:text-[24px] font-bold mb-4 md:mb-6 text-text-primary px-2 md:px-0">
          Debt Relief Enrollment
        </div>
        <div className="flex flex-col lg:flex-row">
          <div className="border border-divider bg-surface shadow-lg rounded-xl lg:rounded-2xl lg:max-w-[280px] h-fit mb-4 lg:mb-0">
            <div className="flex lg:flex-col overflow-x-auto lg:overflow-visible p-1 lg:p-0">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`flex items-center whitespace-nowrap py-2 px-3 lg:py-3.5 lg:pl-4 rounded-lg m-1 lg:m-6 min-w-fit
                    ${tab.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04] cursor-pointer'}
                    ${selectedTab === tab.id && !tab.disabled ? 'bg-[color-mix(in_srgb,var(--color-primary-main)_10%,transparent)]' : ''}`}
                  onClick={() => !tab.disabled && setSelectedTab(tab.id)}
                >
                  <div className="hidden lg:block">
                    <tab.icon
                      fill={selectedTab === tab.id && !tab.disabled ? 'var(--color-primary-main)' : ''}
                      bgFill={selectedTab === tab.id && !tab.disabled ? 'var(--color-primary-main)' : ''}
                    />
                  </div>
                  <div
                    className={`font-normal text-[11px] lg:text-[14px] leading-[1.75] lg:pl-[16px] ${selectedTab === tab.id && !tab.disabled ? 'text-primary-main' : 'text-text-secondary'}`}
                  >
                    {tab.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full lg:w-[798px] lg:ml-[24px]">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelfEnrollment
